<script lang="ts" setup>
  import { BngHoleNumberTag, BngHoles } from '~/components/golf'
  import type { Nullable } from '~/core/types'
  import type { Golf } from '~/core/types/golfs'
  import { BngModal } from '~/layouts/components'

  type Props = {
    golf: Golf
  }
  const props = defineProps<Props>()

  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)
  const { user } = storeToRefs(useUserStore())
  const { favorites, toggleFavorite } = useFavorite()
  const { t } = useI18n()
  const router = useRouter()
  const route = useRoute()
  const coordinates = computed(() => [props.golf.position.latitude || 0, props.golf.position.longitude || 0])

  // LEAFLET
  const zoom = ref(13)
  const url = ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
  const attribution = ref('&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors')

  // sanitizeRegExp = /<(?!(br|\/br|p|\/p|b|\/b|u|\/u|ol|\/ol|ul|\/ul|li|\/li|span|\/span))([^>])+>/gi;

  const readMoreContent = ref<Nullable<{ content: string, title: string }>>(null)

  const showMap = ref(true)

  const holes = computed(() => props.golf.courses.map(({ holes }) => holes))

  const isFavorite = computed(() => favorites.value?.data?.map((g) => g.id).includes(props.golf.id))
  const rate = computed(() => props.golf.rating_count >= 1000 ? (props.golf.rating_count / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : props.golf.rating_count)

  function readMore(content: string[] | string, title: string) {
    readMoreContent.value = {
      content: Array.isArray(content) ? `<br>${content.join(', ')}` : content,
      title,
    }
  }

  function truncateParagraph(content: string, wordsAmount: number): { text: string, more: boolean } {
    const words = content.split(' ')
    const ellipsis = words.length <= wordsAmount ? '' : '...'
    return {
      text: `${words.filter((_, k) => k + 1 <= wordsAmount).join(' ')}${ellipsis}`,
      more: words.length >= wordsAmount,
    }
  }

  function onFavoriteButtonClick() {
    if (user.value && props.golf) {
      toggleFavorite(props.golf)
    } else {
      router.push({ query: { ...route.query, modal: 'login' } })
    }
  }

  function truncateArray(items: string[], amount: number): { items: string[], more: boolean } {
    return {
      items: items.filter((_, k) => k + 1 <= amount),
      more: items.length >= amount,
    }
  }

</script>
<template>
  <div class="c-golf-detail">
    <Teleport
      v-if="readMoreContent !== null"
      to="#modal"
    >
      <BngModal
        :foreground="matchDesktop"
        :hide-backdrop="false"
        closable
        @close="readMoreContent = null"
      >
        <div class="read-more-modal">
          <h3>
            <span>{{ readMoreContent.title }}</span>
          </h3>
          <div class="read-more-modal-content">
            <p v-html="readMoreContent.content" />
          </div>
        </div>
      </BngModal>
    </Teleport>
    <Carousel
      id="golf-detail-carousel"
      :images="props.golf.photos"
    />
    <div class="details">
      <div class="head section">
        <div class="content-left">
          <div class="titles">
            <h1>
              <span>{{ props.golf.name }}</span>
            </h1>
            <p class="subtitle">
              <span>{{ props.golf.address }}</span>
            </p>
          </div>
          <div class="holes">
            <BngHoles
              small
              :model-value="holes"
            />
          </div>
        </div>
        <div class="content-right">
          <div class="rates">
            <template v-if="props.golf.rating">
              <SvgIcon
                icon="star"
                :width="32"
                :height="32"
                :desktop-width="32"
                :desktop-height="32"
              />
              <span class="note">
                <strong>{{ props.golf.rating }}</strong>/5
              </span>
              <span class="rates-count"> ({{ rate }} notes) </span>
            </template>
            <template v-else>
              <span class="no-rate">Aucun avis</span>
            </template>
          </div>
          <button
            class="favorite"
            @click.prevent="onFavoriteButtonClick"
          >
            <SvgIcon
              :class="{ active: isFavorite }"
              :icon="isFavorite ? 'favorite' : 'favorite-stroke'"
              :width="isFavorite ? 28 : 30"
              :height="isFavorite ? 24 : 26"
              :desktop-width="isFavorite ? 28 : 30"
              :desktop-height="isFavorite ? 24 : 26"
            />
          </button>
        </div>
      </div>
      <Divider />
      <div
        v-if="props.golf.description"
        class="description section"
      >
        <h2>
          <span>{{ t("common.description") }}</span>
        </h2>
        <div
          class="txt"
          v-html="truncateParagraph(props.golf.description, 70).text"
        />
        <a
          v-if="truncateParagraph(props.golf.description, 70).more"
          href="javascript:void(0);"
          class="read-more"
          @click="readMore(props.golf.description, t('common.description'))"
        >
          {{ t("common.read-more") }}
        </a>
      </div>
      <Divider v-if="props.golf.description" />
      <div class="services-and-stuffs section">
        <div
          v-if="props.golf.services && props.golf.services.length"
          class="content"
        >
          <h2>
            <span>{{ t("common.services-and-stuffs") }}</span>
          </h2>
          <div class="services-and-stuffs-list">
            <p
              v-for="(service, index) in truncateArray(props.golf.services, 6).items"
              :key="index"
            >
              <span>{{ service }}</span>
            </p>
          </div>
        </div>
        <a
          v-if="truncateArray(props.golf.services, 6).more"
          href="javascript:void(0);"
          class="display-more"
          @click="readMore(props.golf.services, t('common.services-and-stuffs'))"
        >
          {{ t("golf.display-all-services") }}
        </a>
      </div>
      <Divider />
      <div class="courses section">
        <h2>
          <span>{{ t("golf.courses") }}</span>
        </h2>
        <div
          v-for="course in props.golf.courses"
          :key="course.name"
          class="course"
        >
          <div class="course-description">
            <h3>
              <span>{{ course.name }}</span>
            </h3>
            <div
              v-if="course.description"
              class="txt"
              v-html="truncateParagraph(course.description, 20).text"
            />
          </div>
          <div class="course-details">
            <BngHoleNumberTag :hole-number="course.holes" />
            <a
              v-if="truncateParagraph(course.description, 20).more"
              href="javascript:void(0);"
              class="read-more"
              @click="readMore(course.description, course.name)"
            >
              {{ t("common.read-more") }}
            </a>
          </div>
        </div>
      </div>
      <Divider />
      <div
        v-if="props.golf.conditions"
        class="conditions section"
      >
        <h2>
          <span>{{ t("golf.special-conditions") }}</span>
        </h2>
        <div
          class="txt"
          v-html="truncateParagraph(props.golf.conditions, 70).text"
        />
        <a
          v-if="truncateParagraph(props.golf.conditions, 70).more"
          href="javascript:void(0);"
          class="read-more"
          @click="readMore(props.golf.conditions, t('golf.special-conditions'))"
        >
          {{ t("common.read-more") }}
        </a>
      </div>
      <Divider v-if="props.golf.conditions" />
      <div class="location section">
        <h2>
          <span>{{ t("golf.localisation") }}</span>
        </h2>
        <div class="map">
          <LMap
            v-if="showMap"
            :zoom="zoom"
            :center="coordinates"
            :options="{ zoomSnap: 0.5 }"
            :use-global-leaflet="false"
            :max-zoom="16"
            :min-zoom="10"
            :style="{ height: '100%' }"
          >
            <LTileLayer
              :url="url"
              :attribution="attribution"
            />
            <LMarker :lat-lng="coordinates">
              <LIcon
                class-name=""
                :icon-size="[16, 16]"
                :icon-anchor="[8, 8]"
              >
                <span
                  class="circle"
                />
              </LIcon>
            </LMarker>
          </LMap>
        </div>
      </div>
      <div
        v-if="props.golf.location"
        class="access section"
      >
        <h2>
          <span>{{ t("golf.access") }}</span>
        </h2>
        <div
          class="txt"
          v-html="truncateParagraph(props.golf.location, 70).text"
        />
        <a
          v-if="truncateParagraph(props.golf.location, 70).more"
          class="read-more"
          href="javascript:void(0);"
          @click="readMore(props.golf.location, t('golf.access'))"
        >{{ t("common.read-more") }}</a>
      </div>
      <Divider />
      <div class="coordinates section">
        <h2>
          <span>{{ t("common.coordinates") }}</span>
        </h2>
        <h3>
          <span>{{ props.golf.name }}</span>
        </h3>
        <p>{{ props.golf.address }}</p>
        <p>{{ props.golf.commune }} {{ props.golf.country }}</p>

        <a
          class="action"
          type="phone"
        >{{ props.golf.phone }}</a>
        <a
          class="action"
          type="email"
        >{{ props.golf.email }}</a>
      </div>
    </div>
  </div>
</template>
<style scoped>

.read-more-modal {
  display: flex;
  overflow: hidden;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 1.5em;
  font-size: 1rem;

  h3 {
    span {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 700;
      opacity: 0.8;
    }

    padding-bottom: 1em;
  }

  .read-more-modal-content {
    height: 100%;
    overflow-y: scroll;

    p {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 0.875em;
      font-weight: normal;
      line-height: initial;
      opacity: 0.8;
    }
  }
}

.c-golf-detail {
  display: flex;
  width: 100%;
  flex-direction: column;
  background-color: white;

  .c-carousel {
    height: calc(100vw / 2);
    min-height: 200px;
    max-height: 400px;
  }

  .details {
    position: relative;
    width: 100%;
    padding: 1.5em;

    .c-divider {
      margin: 1.5em 0;
    }

    .section {
      display: flex;
      flex-direction: column;

      h1 {
        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1.625em;
          font-weight: 700;
        }
      }

      .subtitle {
        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1em;
          font-weight: 600;
          opacity: 0.8;
        }
      }

      h2 {
        margin-bottom: 1.125em;

        span {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 1.375em;
          font-weight: 700;
          opacity: 0.8;
        }
      }

      h3 {
        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1em;
          font-weight: 700;
          opacity: 0.8;
        }
      }

      p,
      div.txt,
      ul span {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 0.875em;
        font-weight: normal;
        line-height: initial;
        opacity: 0.8;
      }

      a {
        align-self: flex-end;
        color: var(--color-primary);
        font-family: Open-Sans, sans-serif;
        font-size: 0.875em;
        font-weight: normal;
        line-height: initial;
        text-decoration: underline;
      }

      &.head {
        display: flex;
        flex-direction: row;

        .content-left {
          display: flex;
          width: 100%;
          flex-direction: column;
          justify-content: space-between;

          .titles {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
          }

          .holes {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            margin-top: 1em;

            .c-holes {
              flex-wrap: nowrap;
            }
          }
        }

        .content-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-start;

          .rates {
            display: flex;
            flex-direction: row;
            align-items: flex-end;
            padding: 0.25em 0;

            .c-icon {
              margin-right: 0.25em;
              color: var(--color-primary);
            }

            .note,
            .rates-count,
            .no-rate {
              margin: 0 0.25em;
              color: var(--color-secondary);
              font-family: Open-Sans, sans-serif;
              font-size: 0.875em;
              font-weight: 500;
              line-height: 1rem;
              opacity: 0.8;
              white-space: nowrap;

              strong {
                color: var(--color-secondary);
                font-family: Bariol, sans-serif;
                font-size: 1.875em;
                font-weight: 700;
              }

              &.no-rate {
                margin: 0;
              }
            }
          }

          button.favorite {
            display: flex;
            width: 3.125em;
            height: 3.125em;
            align-items: center;
            justify-content: center;
            padding: 0;
            border: none;
            border-radius: 50%;
            margin-top: 1.25em;
            background-color: var(--color-secondary-light);
            font-size: 1em;

            .c-icon {
              color: var(--color-grey-light);
              opacity: 0.5;

              &.active {
                color: var(--color-primary);
                opacity: 1;
              }
            }
          }
        }
      }

      &.services-and-stuffs {
        .content {
          display: flex;
          width: 100%;
          flex-direction: column;

          .services-and-stuffs-list {
            display: grid;
            margin-bottom: 0.5em;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto;

            p {
              margin-bottom: 1em;
            }
          }
        }
      }

      &.courses {
        .course {
          display: flex;
          flex-direction: row;

          &:not(:last-child) {
            margin-bottom: 1.5em;
          }

          .course-description {
            flex: 1 1 auto;
            margin-right: 1em;

            h3 {
              margin-bottom: 0.5em;
              line-height: initial;
            }
          }

          .course-details {
            display: flex;
            flex: 0 0 auto;
            flex-direction: column;
            justify-content: space-between;
          }
        }
      }

      &.location {
        .map {
          position: relative;
          z-index: 1;
          height: 200px;
          margin-bottom: 1.5em;

          .circle {
            display: flex;
            width: 100%;
            height: 100%;
            border-radius: 100%;
            background-color: var(--color-secondary);
          }
        }
      }

      &.coordinates {
        a {
          align-self: flex-start;
        }
      }
    }
  }
}

@media screen and (width >= 1024px) {
  .read-more-modal {
    h3 {
      span {
        font-size: 1.25em;
      }
    }

    .read-more-modal-content {
      p {
        font-size: 1em;
      }
    }
  }

  .c-golf-detail {
    flex: 1 0 auto;
    margin-right: 1.5em;

    .c-carousel {
      height: 325px;
    }

    .details {
      padding: 2.5em;

      .c-divider {
        margin: 2.5em 0;
      }

      .section {
        h1 {
          margin-bottom: 0.5em;

          span {
            font-size: 2em;
          }
        }

        .subtitle {
          span {
            font-size: 1.125em;
          }
        }

        h2 {
          span {
            font-size: 1.5em;
          }
        }

        h3 {
          span {
            font-size: 1.25em;
          }
        }

        p,
        ul span {
          font-size: 1em;
        }

        a {
          margin-top: 1rem;
          font-size: 1em;
        }

        &.services-and-stuffs {
          .content {
            li {
              margin-bottom: 1em;
            }
          }
        }

        &.courses {
          .course {
            justify-content: space-between;

            .course-description {
              max-width: 600px;
              margin-right: 4em;
            }
          }
        }

        &.location {
          .map {
            height: 300px;
          }
        }

        &.coordinates {
          h3 {
            margin-bottom: 1em;
          }
        }
      }
    }
  }
}
</style>
